// Client-side helpers for the SEO landing page fixture lists. Pulls from the
// same shared MOCK_FIXTURES source the prerender uses, so dev and production
// stay in lockstep.

import { MOCK_FIXTURES } from "../../../shared/mock-fixtures";
import { WC2026_TEAMS } from "../../../server/teams";

export type SeoFixture = {
  id: number;
  kickoffUtc: number;
  kickoffIso: string;
  venue: string;
  city: string;
  stage: string;
  groupName: string | null;
  homeTeam: { code: string; name: string; flag: string };
  awayTeam: { code: string; name: string; flag: string };
};

function teamByCode(code: string) {
  return WC2026_TEAMS.find((tm) => tm.code === code);
}

function toSeoFixture(f: typeof MOCK_FIXTURES[number]): SeoFixture | null {
  const home = teamByCode(f.homeTeamCode);
  const away = teamByCode(f.awayTeamCode);
  if (!home || !away) return null;
  return {
    id: f.id,
    kickoffUtc: f.kickoffUtc,
    kickoffIso: new Date(f.kickoffUtc * 1000).toISOString(),
    venue: f.venue,
    city: f.city,
    stage: f.stage,
    groupName: f.groupName,
    homeTeam: { code: home.code, name: home.name, flag: home.flag },
    awayTeam: { code: away.code, name: away.name, flag: away.flag },
  };
}

// Group-stage fixtures for one team, sorted by kickoff.
export function teamGroupStageFixtures(teamCode: string): SeoFixture[] {
  return MOCK_FIXTURES
    .filter(
      (f) =>
        f.stage === "Group Stage" &&
        (f.homeTeamCode === teamCode || f.awayTeamCode === teamCode),
    )
    .map(toSeoFixture)
    .filter((f): f is SeoFixture => f !== null)
    .sort((a, b) => a.kickoffUtc - b.kickoffUtc);
}

// All six group-stage fixtures for a group letter (e.g. "A").
export function groupStageFixtures(groupLetter: string): SeoFixture[] {
  const label = `Group ${groupLetter.toUpperCase()}`;
  return MOCK_FIXTURES
    .filter((f) => f.stage === "Group Stage" && f.groupName === label)
    .map(toSeoFixture)
    .filter((f): f is SeoFixture => f !== null)
    .sort((a, b) => a.kickoffUtc - b.kickoffUtc);
}

// Locale-aware date formatter, matched to the prerender helper.
const INTL_LOCALE: Record<string, string> = {
  en: "en-GB", es: "es-ES", ar: "ar", fr: "fr-FR", pt: "pt-PT", de: "de-DE",
  nl: "nl-NL", hr: "hr-HR", fa: "fa-IR", cs: "cs-CZ", no: "nb-NO", sv: "sv-SE",
  ko: "ko-KR", ja: "ja-JP", tr: "tr-TR", uz: "uz-Latn-UZ",
};

export function formatFixtureDateTime(lang: string, iso: string): string {
  try {
    const tag = INTL_LOCALE[lang] || "en-GB";
    return new Intl.DateTimeFormat(tag, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
      timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
